import { NavigateFunction } from "react-router-dom";
import TagManager from 'react-gtm-module';
import { AuthenticationResultType } from "aws-sdk/clients/cognitoidentityserviceprovider";
import * as IdbKeyVal from "idb-keyval";
import * as CryptoJs from 'crypto-js';
import * as CognitoService from '../services/CognitoService';
import * as SignatureService from "../services/SignatureService";
import * as GrindTables from '../library/GrindTables';
import * as WedgeOrderHelper from '../library/WedgeOrderHelper';
import * as CountryListHelper from '../library/CountryListHelper';
import axiosInstance from '../services/AxiosService';
import Fitter from '../classes/Fitter';
import { LocaleType } from "../classes/Locale";

const GOLFER_ID = "golferId";
const FITTER_ID = "fitterId";
const FITTER_LIST = "fitterList";

//if user successfully signs in, just return with void. If bad password or no network connectivity throw error
export const signIn = async (email: string, password: string, iOS: boolean) => {
    let authenticationResult!: AuthenticationResultType;
    let poolCheck!: SignatureService.PoolCheck;
    let passwordIsValid = false;
    let newFitter = new Fitter();
    let signInError: Error = new Error();

    const fitter = await getFitterByEmail(email);

    // if user is not online then check if their password matches the encrypted password
    if (!window.navigator.onLine) {
        // compare the password they entered to the one in local storage
        if (fitter) {
            passwordIsValid = verifyPW(password, fitter.password);
            if (passwordIsValid) {
                IdbKeyVal.set(FITTER_ID, fitter.email);
                newFitter = fitter;
            }
        }
        // if not valid password, still throw network error b/c no way to tell if the new password matches the most recent one on the backend since we aren't online
        // e.g. it might actually be a valid password if they changed it recently and we just don't know since we're offline
        if (!passwordIsValid) {
            throw (new Error('NetworkError'));
        }

        return newFitter;
    }

    try {
        if (fitter?.userPool) {
            // IsConfirmed = true since they have logged in before
            poolCheck = { IsConfirmed: true, UserPool: fitter?.userPool === 1 ? "Main" : "PWA" }
        } else {
            poolCheck = await SignatureService.poolCheck(email);
        }
    } catch (err) {
        signInError = err as Error;
        TagManager.dataLayer({
            dataLayer: {
                event: 'log_error',
                description: 'Fitter Helper - Pool Check',
                msg: 'catch: ' + err
            }
        });
        throw (signInError);
    }

    if (poolCheck.UserPool === 'None') {
        signInError = new Error('User does not belong to any pool');
        TagManager.dataLayer({
            dataLayer: {
                event: 'log_error',
                description: 'Fitter Helper - Missing User Pool',
                msg: 'catch: ' + signInError
            }
        });
        throw (signInError);
    }

    try {
        authenticationResult = await CognitoService.signInUser(email, password, poolCheck.UserPool)
    }
    catch (err) {
        signInError = err as Error;
        TagManager.dataLayer({
            dataLayer: {
                event: 'log_error',
                description: 'Fitter Helper - Sign in',
                msg: 'catch: ' + err
            }
        });

        if (!poolCheck.IsConfirmed) {
            await CognitoService.resendEmailConfirmationCode(email, poolCheck.UserPool)
            signInError.message = 'Unverified Email'
        }

        throw (signInError);
    }

    if (authenticationResult) {
        await saveLoginDateStamp();
        //        let userSession = cognitoUserSession as CognitoUserSession;

        //successful login
        let idToken = authenticationResult.IdToken!;
        let accessToken = authenticationResult.AccessToken!;

        let refreshToken = authenticationResult.RefreshToken!;
        let awsIdToken = authenticationResult.IdToken || "";

        let payload = decodeToken(awsIdToken);
        var deviceKey = payload["device_key"] || "";
        if (deviceKey === "") {
            deviceKey = (window.crypto as any).randomUUID().substring(14, 18); //random substring from GUID
        }
        let authTimeEpoch = Number(payload["auth_time"]);
        let cognitoUserName = payload["cognito:username"] || "";
        let accountNumber = payload["custom:ShipToNumber"] || "";
        let fitterId = Number(payload["custom:FitterID"]) || 12006;
        let locationID = Number(payload["custom:LocationID"] || "-1");
        var groups = "";
        let groupsArray = payload["cognito:groups"];
        if (groupsArray && groupsArray.length > 0) {
            groups = groupsArray.join(", ");
        }
        let timezone = payload["custom:Timezone"] || "";
        let fitterName = payload["name"];
        let fitterEmail = payload.email;
        let fitterPassword = encryptPW(password);
        let fitterFirstName = payload.given_name || "";
        let fitterLastName = payload.family_name || "";

        const { regionId, country, locale: payloadLocale, locationName: payloadLocationName } = normalizeFitterLocation({
            awsRegionId: payload["custom:RegionId"],
            awsCountryCode: payload["custom:CountryCode"],
            awsLocale: payload["custom:Locale"],
            awsLocationName: payload["custom:LocationName"],
        });

        let locationName = payloadLocationName;
        // get location name from cognito
        if (locationID != -1) {
            locationName = await WedgeOrderHelper.getLocationName(locationID, awsIdToken);
        }

        //if this is a Dick's Sporting Good location, set hideEmail default to true
        let hideGolferEmail = (fitterEmail.indexOf("@dcsg.com") > -1 || fitterEmail.indexOf("@golfgalaxy.com") > -1);

        //1 update
        var emailIsSm11 = GrindTables.emailIsSm11(email);

        //If the fitter has logged in before, we don't want to overwrite their settings
        let prevFitter = await getFitterByEmail(email);
        if (prevFitter && (prevFitter.deviceKey === undefined || prevFitter.deviceKey === "")) {
            prevFitter.deviceKey = deviceKey;
        }
        if (emailIsSm11 && prevFitter && prevFitter.showSm11 === undefined){
            prevFitter.showSm11 = true;
        } else if (prevFitter) {
            prevFitter.showSm11 = true;
        }

        // Using previous pool ids (Main = 1 PWA = 2) for backwards compatibility
        const userPool = poolCheck.UserPool === 'Main' ? 1 : 2;

        // locationName and venueName are both the same
        newFitter = new Fitter(awsIdToken, fitterId, fitterName, fitterName, fitterFirstName, fitterLastName, fitterEmail, fitterPassword, 3, "Standard", "MPH", "Yards",
            true, true, true, "English", regionId, locationID, locationName, country,
            payloadLocale as LocaleType, timezone, locationName, accountNumber, hideGolferEmail, cognitoUserName, userPool, authTimeEpoch, refreshToken, iOS, deviceKey, true, groups, emailIsSm11);
        // overwrite default settings with previous settings
        if (prevFitter !== undefined) {
            newFitter = {
                ...newFitter, refreshToken: refreshToken, awsIdToken: awsIdToken, shotsPerClub: prevFitter.shotsPerClub, fittingType: prevFitter.fittingType, speedUnits: prevFitter.speedUnits, distanceUnits: prevFitter.distanceUnits,
                useEstimateDynamicLoft: prevFitter.useEstimateDynamicLoft, useEstimateNoClubMeasurement: prevFitter.useEstimateNoClubMeasurement, showCarry: prevFitter.showCarry, language: prevFitter.language,
                hideGolferEmail: prevFitter.hideGolferEmail, deviceKey: prevFitter.deviceKey, firstLogin: prevFitter.firstLogin, showSm11: prevFitter.showSm11
            };
        }
        await upsertFitter(newFitter);
    }
    return newFitter;
}

function decodeToken(token: string) {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);    
}

export const getAllFitters = async () => {
    const fitterList = await IdbKeyVal.get<Fitter[]>(FITTER_LIST);
    return fitterList;
}

//get current fitter id from local db
export const getFitterId = async () => {
    const fitterId = await IdbKeyVal.get(FITTER_ID);
    return fitterId;
}

//get current fitter from local db
export const getFitter = async () => {
    let fitter = new Fitter();

    let fitterId = await IdbKeyVal.get(FITTER_ID);
    //if no fitter (should never happen) exit
    if (fitterId === undefined) {
        return fitter;
    }

    const fitterList = await IdbKeyVal.get<Fitter[]>(FITTER_LIST);
    //making transition from FitterID as the key to email address as the key. Thus, need to check for both until all users are upgraded to latest version
    if (isNaN(fitterId)) {
        //Id is email address
        fitter = fitterList?.filter(f => f.email === fitterId)[0] as Fitter;
    } else {
        fitter = fitterList?.filter(f => f.fitterID === fitterId)[0] as Fitter;
    }

    return fitter;
}

export const getFitterByEmail = async (email: string): Promise<Fitter | undefined> => {
    //get all fitters in local storage
    const fitterList = await IdbKeyVal.get<Fitter[]>(FITTER_LIST);

    //has this fitter already logged in before?
    if (fitterList) {
        let fitter = fitterList.find(f => f.email === email);
        return fitter;
    }
    return undefined;
}

//set the fitterId to the current fitter, and update their object in local db
export const upsertFitter = async (fitter: Fitter): Promise<void> => {
    IdbKeyVal.set(FITTER_ID, fitter.email);
    IdbKeyVal.update<Fitter[]>(FITTER_LIST, (existingList) => {

        let fitterList = existingList || new Array<Fitter>();
        let fitterFiltered = fitterList.filter(f => f.email !== fitter.email);
        fitterFiltered.push(fitter);
        return fitterFiltered;
    })
}

export const logout = async () => {
    try {
        await axiosInstance.post(
            `${process.env.REACT_APP_SECURE_API_GATEWAY}/auth/logout`,
            {}
        );
    } catch (err) {
        TagManager.dataLayer({
            dataLayer: {
                event: 'log_error',
                description: 'Fitter Helper - logout',
                msg: 'catch: ' + err
            }
        });
    } finally {
        await IdbKeyVal.del(FITTER_ID);
        await IdbKeyVal.del(GOLFER_ID);
    }
}

export const encryptPW = (password: string) => {
    let value = CryptoJs.AES.encrypt(password, process.env.REACT_APP_KEY_CRYPTO || "").toString();
    return value;
}

export const verifyPW = (password: string, encryptedPassword: string) => {

    let decryptedPassword = CryptoJs.AES.decrypt(encryptedPassword, process.env.REACT_APP_KEY_CRYPTO || "").toString(CryptoJs.enc.Utf8);
    return password === decryptedPassword;
}

interface IFitterLocationPayload {
    awsRegionId: string | null;
    awsCountryCode: string | null;
    awsLocale: string | null;
    awsLocationName: string | null;
}

const normalizeFitterLocation = (payload: IFitterLocationPayload) => {
    const { awsRegionId, awsCountryCode, awsLocale, awsLocationName } = payload;

    // Default values
    let country: string = "US";
    let locationName: string = "";
    const locale: string = awsLocale || "en-US";

    if (awsLocale) {
        // User Pool 1: Locale gives us country code
        country = awsLocale.slice(- 2) || "US";
        const countryObj = CountryListHelper.list.find(c => c.id === country);

        if (awsRegionId && Number(awsRegionId) !== countryObj?.region) {
            const fallbackCountry = CountryListHelper.defaultRegionCountryList[Number(awsRegionId)];
            country = fallbackCountry.id;
        }
    } else {
        // User Pool 2 fallback
        locationName = awsLocationName || "";
        country = awsCountryCode || "US";
    }

    // Resolve region ID with fallback
    const regionObj = CountryListHelper.list.find(c => c.id === country);
    const regionId = awsRegionId || `${regionObj?.region}` || "1";

    return {
        regionId,
        country,
        locale,
        locationName,
    }
}

// Fitter Login Helper

//this code is valid, but the backend always rejects any new auth keys. Debug with Bill in the club fitting app. For now just re-do the login behind the scenes
export const refreshAuthToken = async () => {
    //get current fitter info
    let fitter = await getFitter();

    try {
        let authenticationResult = await CognitoService.refreshAuthToken(fitter.refreshToken, fitter.userPool);
        let newToken = authenticationResult?.IdToken || "";

        if (newToken != "") {
            fitter.awsIdToken = newToken;
            let currentTime = new Date().getTime();
            fitter.authTimeEpoch = currentTime / 1000;
            //save new token and time to localDb
            await upsertFitter(fitter);
        }
    }
    catch (err) {
        TagManager.dataLayer({
            dataLayer: {
                event: 'log_error',
                description: 'Fitter Helper - refresh auth token',
                msg: 'catch: ' + err
            }
        });
    }
    return fitter.awsIdToken;
}

export const getFitterJwt = async () => {
    var fitter = await getFitter();
    var jwtToken = fitter.awsIdToken;
    let currentTime = new Date().getTime();
    //50 minute expiration for jwt
    if ((jwtToken === "" || (currentTime / 1000 - fitter.authTimeEpoch) / 60 > 50)) {
        jwtToken = await refreshAuthToken();
    }

    return jwtToken;
}

const FITTER_LOGIN_DATE_STAMP = "vwf-fitterLoginDateStamp";

const saveLoginDateStamp = async () => {
    // Date Format: YYYY-MM-DD
    const loginDateStamp = (new Date()).toISOString().split('T')[0];
    await IdbKeyVal.set(FITTER_LOGIN_DATE_STAMP, loginDateStamp);
}

const isLastLoginOlderThanToday = async () => {
    const lastLoginDateStamp: string = await IdbKeyVal.get(FITTER_LOGIN_DATE_STAMP) || "";
    const todayDateStamp = (new Date()).toISOString().split('T')[0];
    return todayDateStamp > lastLoginDateStamp;
}

// Return true/ false to capture the output of the function
// Since we want to redirect, and navigate is async, we need to avoid other possible navigate executions after this one
export const logOutIfExpiredLogin = async (navigate: NavigateFunction) => {
    const expiredLogin = await isLastLoginOlderThanToday();
    if (expiredLogin) {
      await logout();
      navigate("/apps/wedgefitting", { state: { isForcedFitterLogin: true }});
      return true;
    }
    return false;
}
